import type { SupabaseClient } from '@supabase/supabase-js';
import { AppError, toAppError } from '../utils/appErrors';
import { getSupabaseClient } from './supabase';

type ClientOption = { supabase?: SupabaseClient };

export type ScheduleColorOwnerKind = 'event' | 'installation' | 'ticket';

export type TakenScheduleColor = {
  color: string;
  kind: ScheduleColorOwnerKind | 'shift';
  targetId: string;
  label: string;
  startDate: string | null;
  endDate: string | null;
};

export type TakenScheduleColorsRequest =
  | { scope: 'link'; kind: ScheduleColorOwnerKind; targetId: string }
  | {
    scope: 'shift';
    startsAt: string;
    endsAt: string | null;
    locationId: string;
    eventIds: string[];
    installationJobId: string | null;
    serviceTicketId: string | null;
  };

export const scheduleColorKeys = {
  all: ['schedule-colors'] as const,
  link: (kind: ScheduleColorOwnerKind, targetId: string) => ['schedule-colors', 'link', kind, targetId] as const,
  taken: (request: TakenScheduleColorsRequest) => ['schedule-colors', 'taken', request] as const,
};

const OWNER_SOURCE: Record<ScheduleColorOwnerKind, { table: string; idColumn: string }> = {
  event: { table: 'events', idColumn: 'id' },
  installation: { table: 'installation_jobs', idColumn: 'installation_job_id' },
  ticket: { table: 'service_tickets', idColumn: 'service_ticket_id' },
};

function textOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/**
 * The database rejects a color another job owns on overlapping dates with
 * `schedule_color_taken`; its detail names that job and its dates.
 */
export function scheduleColorTakenError(value: unknown): AppError | null {
  if (!value || typeof value !== 'object') return null;
  const error = value as { message?: unknown; details?: unknown };
  if (error.message !== 'schedule_color_taken') return null;
  const owner = textOrNull(error.details);
  return new AppError(
    'validation',
    owner ? `That color is already used by ${owner}. Pick another color.` : 'That color is already in use. Pick another color.',
    { code: 'P0001', cause: value },
  );
}

export async function fetchScheduleLinkColor(
  kind: ScheduleColorOwnerKind,
  targetId: string,
  options?: ClientOption,
): Promise<string | null> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const source = OWNER_SOURCE[kind];
  const { data, error } = await supabase
    .from(source.table)
    .select('labor_calendar_color')
    .eq(source.idColumn, targetId)
    .maybeSingle();
  if (error) throw toAppError(error, 'Unable to load the Schedule color.');
  return textOrNull((data as { labor_calendar_color?: unknown } | null)?.labor_calendar_color);
}

/** Saves the color every linked shift inherits; null returns them to automatic. */
export async function setScheduleLinkColor(
  kind: ScheduleColorOwnerKind,
  targetId: string,
  color: string | null,
  options?: ClientOption,
): Promise<string | null> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.rpc('labor_schedule_set_link_color', {
    p_kind: kind,
    p_target_id: targetId,
    p_color: color,
  });
  if (error) throw scheduleColorTakenError(error) ?? toAppError(error, 'Unable to save the Schedule color.', undefined, 'mutation');
  return textOrNull(data);
}

function shortDate(value: string): string {
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Color -> "Used by Expo 2026 (Oct 5 – Oct 9)" for the picker's greyed-out swatches. */
export function takenScheduleColorReasons(rows: readonly TakenScheduleColor[]): Map<string, string> {
  const reasons = new Map<string, string>();
  for (const row of rows) {
    if (reasons.has(row.color)) continue;
    const dates = row.startDate
      ? (!row.endDate || row.endDate === row.startDate ? shortDate(row.startDate) : `${shortDate(row.startDate)} – ${shortDate(row.endDate)}`)
      : '';
    reasons.set(row.color, `Used by ${row.label}${dates ? ` (${dates})` : ''}`);
  }
  return reasons;
}

/** Colors other jobs (and, for a job, other shifts) own on overlapping dates. */
export async function fetchTakenScheduleColors(
  request: TakenScheduleColorsRequest,
  options?: ClientOption,
): Promise<TakenScheduleColor[]> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const params = request.scope === 'link'
    ? { p_kind: request.kind, p_target_id: request.targetId }
    : {
      p_kind: 'shift',
      p_starts_at: request.startsAt,
      p_ends_at: request.endsAt,
      p_location_id: request.locationId,
      p_event_ids: request.eventIds,
      p_installation_job_id: request.installationJobId,
      p_service_ticket_id: request.serviceTicketId,
    };
  const { data, error } = await supabase.rpc('labor_schedule_taken_colors', params);
  if (error) throw toAppError(error, 'Unable to check which colors are in use.');
  return (Array.isArray(data) ? data : []).flatMap((row): TakenScheduleColor[] => {
    const value = row as Record<string, unknown>;
    const color = textOrNull(value.color)?.toLowerCase();
    const kind = textOrNull(value.kind);
    const targetId = textOrNull(value.target_id);
    if (!color || !targetId || !kind || !['event', 'installation', 'ticket', 'shift'].includes(kind)) return [];
    return [{
      color,
      kind: kind as TakenScheduleColor['kind'],
      targetId,
      label: textOrNull(value.label) ?? 'Another job',
      startDate: textOrNull(value.start_date),
      endDate: textOrNull(value.end_date),
    }];
  });
}
